import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

export default function WithdrawPasswordCard() {
  const [hasPwd, setHasPwd] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showChange, setShowChange] = useState(false);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('has_withdraw_password');
      if (rpcError) throw rpcError;
      setHasPwd(Boolean(data));
    } catch (err) {
      const msg = (err instanceof Error ? err.message : (err as { message?: string })?.message) || 'Không thể tải trạng thái.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (hasPwd && showChange) {
      if (!currentPwd) {
        setError('Vui lòng nhập mật khẩu hiện tại.');
        return;
      }
      const { data: ok, error: verifyError } = await supabase.rpc('verify_withdraw_password', {
        p_password: currentPwd,
      });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      if (!ok) {
        setError('Mật khẩu hiện tại không đúng.');
        return;
      }
    }

    if (!newPwd || newPwd.length < 6) {
      setError('Mật khẩu mới phải có ít nhất 6 ký tự.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('Xác nhận mật khẩu không khớp.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: saveError } = await supabase.rpc('set_withdraw_password', { p_password: newPwd });
      if (saveError) throw saveError;

      setSuccess(hasPwd ? 'Đã đổi mật khẩu rút tiền thành công.' : 'Đã tạo mật khẩu rút tiền thành công.');
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setShowChange(false);
      setShowNew(false);
      setShowConfirm(false);
      setShowCurrent(false);
      await load();
    } catch (err) {
      const msg = (err instanceof Error ? err.message : (err as { message?: string })?.message) || 'Không thể lưu mật khẩu.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 pr-10 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500';
  const labelCls = 'block text-xs font-semibold text-foreground-600 mb-1.5';

  const renderPwdField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    show: boolean,
    toggleShow: () => void
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputCls}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={toggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-foreground-900 cursor-pointer"
          aria-label={show ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
        >
          <i className={`${show ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
        </button>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-background-200/60 bg-background-100 p-6 mb-6">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-secondary-500/15 flex items-center justify-center shrink-0">
          <i className="ri-lock-password-line text-secondary-500 text-2xl"></i>
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground-950">Mật khẩu rút tiền</h2>
          <p className="text-sm text-foreground-600 mt-0.5">
            Tăng cường bảo mật: mỗi lần rút tiền đều phải nhập đúng mật khẩu này.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-rose-400 text-base"></i>
          <p className="text-sm text-rose-400">{error}</p>
        </div>
      )}
      {success && (
        <div className="mt-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
          <i className="ri-check-line text-emerald-400 text-base"></i>
          <p className="text-sm text-emerald-400">{success}</p>
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-foreground-600">Đang tải...</p>
      ) : !hasPwd ? (
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          {renderPwdField('Mật khẩu mới', newPwd, setNewPwd, 'Tối thiểu 6 ký tự', showNew, () => setShowNew((v) => !v))}
          {renderPwdField('Xác nhận mật khẩu', confirmPwd, setConfirmPwd, 'Nhập lại mật khẩu mới', showConfirm, () => setShowConfirm((v) => !v))}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
          >
            {submitting ? 'Đang lưu...' : 'Tạo mật khẩu rút tiền'}
          </button>
        </form>
      ) : (
        <div className="mt-5">
          {!showChange ? (
            <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-4 py-3">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-emerald-400 text-base"></i>
                <span className="text-sm font-semibold text-emerald-400">Đã đặt mật khẩu rút tiền</span>
              </div>
              <button
                type="button"
                onClick={() => setShowChange(true)}
                className="text-sm font-semibold text-accent-400 hover:text-accent-300 whitespace-nowrap cursor-pointer"
              >
                Đổi mật khẩu
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {renderPwdField('Mật khẩu hiện tại', currentPwd, setCurrentPwd, 'Nhập mật khẩu hiện tại', showCurrent, () => setShowCurrent((v) => !v))}
              {renderPwdField('Mật khẩu mới', newPwd, setNewPwd, 'Tối thiểu 6 ký tự', showNew, () => setShowNew((v) => !v))}
              {renderPwdField('Xác nhận mật khẩu', confirmPwd, setConfirmPwd, 'Nhập lại mật khẩu mới', showConfirm, () => setShowConfirm((v) => !v))}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {submitting ? 'Đang lưu...' : 'Cập nhật mật khẩu'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowChange(false);
                    setCurrentPwd('');
                    setNewPwd('');
                    setConfirmPwd('');
                  }}
                  className="px-4 py-3 rounded-md bg-background-200/50 text-foreground-700 font-semibold text-sm hover:bg-background-200 whitespace-nowrap cursor-pointer transition-colors"
                >
                  Hủy
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}